import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { UserResponse, SubscriptionFormData, UpdateSubscriptionFormData } from '../types/api';
import { useTranslation } from '../context/LanguageContext';
import { toGreekUpperCase } from '../utils/greekUtils';
import SubscriptionHistoryModal from './SubscriptionHistoryModal';
import './Modal.css';

interface EditClientSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientUpdated: () => void;
  client: UserResponse | null;
}

const EditClientSubscriptionModal: React.FC<EditClientSubscriptionModalProps> = ({ 
  isOpen, 
  onClose, 
  onClientUpdated, 
  client 
}) => {
  const { t, formatDate: formatDateWithLocale } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Create subscription form
  const [createSubscriptionInfo, setCreateSubscriptionInfo] = useState<SubscriptionFormData>({
    startDate: '',
    endDate: '',
    monthlyLimit: 12,
    isActive: true,
  });

  // Update subscription form
  const [updateSubscriptionInfo, setUpdateSubscriptionInfo] = useState<UpdateSubscriptionFormData>({
    startDate: '',
    endDate: '',
    actualStartDate: '',
    actualEndDate: '',
    monthlyLimit: 12,
    isActive: true,
  });

  useEffect(() => {
    if (isOpen && client) {
      // Reset create form to defaults
      setCreateSubscriptionInfo({
        startDate: '',
        endDate: '',
        monthlyLimit: 12,
        isActive: true,
      });

      if (client.subscription) {
        // Populate update form with existing subscription data
        setUpdateSubscriptionInfo({
          startDate: client.subscription.startDate.split('T')[0],
          endDate: client.subscription.endDate.split('T')[0],
          // Leave blank while the subscription hasn't been activated yet - filling these in
          // from the planned dates would silently start the subscription clock on save.
          actualStartDate: client.subscription.actualStartDate?.split('T')[0] || '',
          actualEndDate: client.subscription.actualEndDate?.split('T')[0] || '',
          monthlyLimit: client.subscription.monthlyLimit,
          isActive: client.subscription.isActive,
        });
      }
      
      // Clear previous messages
      setError('');
      setSuccess('');
    }
  }, [isOpen, client]);

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.createClientSubscription(client.id, createSubscriptionInfo);
      if (response.success) {
        setSuccess(t('subscriptionCreatedSuccessfully'));
        onClientUpdated();
        // Close modal after short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.message || t('failedToCreateSubscription'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorCreatingSubscription'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client || !client.subscription) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.updateClientSubscription(client.id, {
        ...updateSubscriptionInfo,
        actualStartDate: updateSubscriptionInfo.actualStartDate || null,
        actualEndDate: updateSubscriptionInfo.actualEndDate || null,
      });
      if (response.success) {
        setSuccess(t('subscriptionUpdatedSuccessfully'));
        onClientUpdated();
        // Close modal after short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.message || t('failedToUpdateSubscription'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorUpdatingSubscription'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFreezeSubscription = async () => {
    if (!client?.subscription) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.freezeClientSubscription(client.subscription.id);
      if (response.success) {
        setSuccess(t('subscriptionFrozenSuccessfully'));
        onClientUpdated();
        // Close modal after short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.message || t('failedToFreezeSubscription'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorFreezingSubscription'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnfreezeSubscription = async () => {
    if (!client?.subscription) return;
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.unfreezeClientSubscription(client.subscription.id);
      if (response.success) {
        setSuccess(t('subscriptionUnfrozenSuccessfully'));
        onClientUpdated();
        // Close modal after short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.message || t('failedToUnfreezeSubscription'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorUnfreezingSubscription'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateWithLocale(dateString, { short: true });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('editSubscription')} - {client?.name}</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setShowHistoryModal(true)}
              className="btn-secondary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {t('viewHistory')}
            </button>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="modal-body">
          {/* Current Subscription Display */}
          {client?.subscription && (
          <div className="current-subscription">
            <h3>{t('currentSubscription')}</h3>
            <div className="subscription-details">
              <div className="subscription-section">
                <h4>{t('eligibilityWindow')}</h4>
                <div className="detail-row">
                  <span className="detail-label">{t('startDate')}:</span>
                  <span className="detail-value">{formatDate(client.subscription.startDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('endDate')}:</span>
                  <span className="detail-value">{formatDate(client.subscription.endDate)}</span>
                </div>
              </div>
              
              <div className="subscription-section">
                <div className="section-header">
                  <h4>{t('activationStatus')}</h4>
                  {client.subscription.isFrozen ? (
                    <button
                      type="button"
                      onClick={handleUnfreezeSubscription}
                      className="btn-unfreeze"
                      disabled={isLoading}
                      title={t('unfreezeSubscription')}
                    >
                      {t('unfreeze')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleFreezeSubscription}
                      className="btn-freeze"
                      disabled={isLoading}
                      title={t('freezeSubscription')}
                    >
                      {t('freeze')}
                    </button>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('status')}:</span>
                  <span className={`detail-value status-badge ${
                    client.subscription.isFrozen ? 'frozen' : 
                    client.subscription.isActivated ? 'activated' : 'waiting'
                  }`}>
                    {client.subscription.isFrozen ? toGreekUpperCase(t('frozen')) : 
                     client.subscription.isActivated ? toGreekUpperCase(t('activated')) : toGreekUpperCase(t('waitingForFirstAppointment'))}
                  </span>
                </div>
                {client.subscription.isFrozen && client.subscription.frozenAt && (
                  <div className="detail-row">
                    <span className="detail-label">{t('frozenAt')}:</span>
                    <span className="detail-value">{formatDate(client.subscription.frozenAt)}</span>
                  </div>
                )}
                {client.subscription.remainingDaysAtFreeze !== undefined && client.subscription.remainingDaysAtFreeze !== null && (
                  <div className="detail-row">
                    <span className="detail-label">{t('remainingDaysAtFreeze')}:</span>
                    <span className="detail-value">{client.subscription.remainingDaysAtFreeze} {t('days')}</span>
                  </div>
                )}
                {client.subscription.actualStartDate && (
                  <div className="detail-row">
                    <span className="detail-label">{t('actualStartDate')}:</span>
                    <span className="detail-value">{formatDate(client.subscription.actualStartDate)}</span>
                  </div>
                )}
                {client.subscription.actualEndDate && (
                  <div className="detail-row">
                    <span className="detail-label">{t('actualEndDate')}:</span>
                    <span className="detail-value">{formatDate(client.subscription.actualEndDate)}</span>
                  </div>
                )}
              </div>
              
              <div className="subscription-section">
                <h4>{t('subscriptionDetails')}</h4>
                <div className="detail-row">
                  <span className="detail-label">{t('monthlyLimit')}:</span>
                  <span className="detail-value">{client.subscription.monthlyLimit} {t('appointmentsPerMonth')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('active')}:</span>
                  <span className="detail-value">{client.subscription.isActive ? t('yes') : t('no')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('validAppointmentCount')}:</span>
                  <span className="detail-value">{client.subscription.validAppointmentCount}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('attendedAppointmentCount')}:</span>
                  <span className="detail-value">{client.subscription.attendedAppointmentCount ?? 0}</span>
                </div>
              </div>
            </div>
          </div>
          )}

          {!client?.subscription && (
            <div className="no-subscription">
              <p>{t('clientHasNoSubscription')}</p>
            </div>
          )}

          {/* Create New Subscription Form - Always show */}
          <form onSubmit={handleCreateSubscription} className="modal-form">
          <h3>{t('createNewSubscription')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="createStartDate">{t('startDate')}</label>
              <input
                type="date"
                id="createStartDate"
                value={createSubscriptionInfo.startDate}
                onChange={(e) => setCreateSubscriptionInfo({...createSubscriptionInfo, startDate: e.target.value})}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="createEndDate">{t('endDate')}</label>
              <input
                type="date"
                id="createEndDate"
                value={createSubscriptionInfo.endDate}
                onChange={(e) => setCreateSubscriptionInfo({...createSubscriptionInfo, endDate: e.target.value})}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="createMonthlyLimit">{t('monthlyLimit')}</label>
            <input
              type="number"
              id="createMonthlyLimit"
              min="1"
              max="50"
              value={createSubscriptionInfo.monthlyLimit}
              onChange={(e) => setCreateSubscriptionInfo({...createSubscriptionInfo, monthlyLimit: parseInt(e.target.value)})}
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={createSubscriptionInfo.isActive}
                onChange={(e) => setCreateSubscriptionInfo({...createSubscriptionInfo, isActive: e.target.checked})}
                disabled={isLoading}
              />
              <span className="checkbox-text">{t('activeSubscription')}</span>
            </label>
          </div>
          
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t('cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? t('creating') : t('createSubscription')}
            </button>
          </div>
          </form>

          {/* Update Existing Subscription Form - Only show if client has subscription */}
          {client?.subscription && (
            <form onSubmit={handleUpdateSubscription} className="modal-form update-form">
            <h3>{t('updateCurrentSubscription')}</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="updateStartDate">{t('startDate')}</label>
                <input
                  type="date"
                  id="updateStartDate"
                  value={updateSubscriptionInfo.startDate}
                  onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, startDate: e.target.value})}
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="updateEndDate">{t('endDate')}</label>
                <input
                  type="date"
                  id="updateEndDate"
                  value={updateSubscriptionInfo.endDate}
                  onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, endDate: e.target.value})}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="updateActualStartDate">{t('actualStartDate')}</label>
                <input
                  type="date"
                  id="updateActualStartDate"
                  value={updateSubscriptionInfo.actualStartDate}
                  onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, actualStartDate: e.target.value})}
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="updateActualEndDate">{t('actualEndDate')}</label>
                <input
                  type="date"
                  id="updateActualEndDate"
                  value={updateSubscriptionInfo.actualEndDate}
                  onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, actualEndDate: e.target.value})}
                  disabled={isLoading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="updateMonthlyLimit">{t('monthlyLimit')}</label>
              <input
                type="number"
                id="updateMonthlyLimit"
                min="1"
                max="50"
                value={updateSubscriptionInfo.monthlyLimit}
                onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, monthlyLimit: parseInt(e.target.value)})}
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={updateSubscriptionInfo.isActive}
                  onChange={(e) => setUpdateSubscriptionInfo({...updateSubscriptionInfo, isActive: e.target.checked})}
                  disabled={isLoading}
                />
                <span className="checkbox-text">{t('activeSubscription')}</span>
              </label>
            </div>
            
            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                {t('cancel')}
              </button>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? t('updating') : t('updateSubscription')}
              </button>
            </div>
            </form>
          )}
        </div>
      </div>

      {/* Subscription History Modal */}
      <SubscriptionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        clientId={client?.id}
        clientName={client?.name}
      />
    </div>
  );
};

export default EditClientSubscriptionModal; 